import { useQuery } from '@tanstack/react-query';

import {
  getV1BusinessPartnerCurrentMigrationCandidateUsersOptions,
  getV1IdentityCurrentMigrationCandidateBusinessPartnersOptions,
  getV1MigrationCandidateBusinessPartnersOptions,
  getV1MigrationCandidateBusinessPartnersQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import { queryClient } from '../../api/queryClient';
import featureFlags from '../../utils/featureFlags';
import {
  migrationCandidateBusinessPartnersMockData,
  migrationCandidateCustomerSuggestionsMockData,
  migrationCandidateUsersMockData,
} from '../../utils/mockData';

/**
 * Query hooks for the TLM Migration feature.
 *
 * Thin wrappers over the generated `@hey-api` react-query options, following the
 * colocated data-fetching pattern (see
 * `pages/Settings/IdentityProviders/identityProviders.queries.ts`). These
 * Migration lists are consumed across Companies, Divisions, Customers, Overview
 * and the route sidebar, so they live in the shared `src/hooks/queries/` home.
 *
 * The `VITE_FF_MIGRATION_MOCK_DATA` override is pre-release scaffolding kept
 * inside each wrapper so it stays trivial to delete once the flag is gone.
 */

/**
 * Identity-scoped migration-candidate business partners ("Unclaimed
 * Organizations").
 *
 * NOTE: The key-factory options used here MUST stay byte-identical to the
 * `useHideUnclaimedOrganizations` cache reader in `src/app/routes/utils.ts`;
 * otherwise the sidebar reads `undefined` and silently hides the entry.
 */
export const useMigrationCandidateBusinessPartners = () => {
  const query = useQuery({
    ...getV1IdentityCurrentMigrationCandidateBusinessPartnersOptions(),
    enabled: featureFlags.MIGRATION && !featureFlags.MIGRATION_MOCK_DATA,
  });

  if (featureFlags.MIGRATION_MOCK_DATA) {
    return { ...query, data: migrationCandidateBusinessPartnersMockData, isLoading: false };
  }

  return query;
};

/**
 * Business-partner-scoped migration-candidate users ("Customer Suggestions").
 *
 * NOTE: The key-factory options used here MUST stay byte-identical to the
 * `useHideCustomerSuggestions` cache reader in `src/app/routes/utils.ts`;
 * otherwise the sidebar reads `undefined` and silently hides that entry.
 */
export const useMigrationCandidateUsers = () => {
  const query = useQuery({
    ...getV1BusinessPartnerCurrentMigrationCandidateUsersOptions(),
    enabled: featureFlags.MIGRATION && !featureFlags.MIGRATION_MOCK_DATA,
  });

  if (featureFlags.MIGRATION_MOCK_DATA) {
    return { ...query, data: migrationCandidateUsersMockData, isLoading: false };
  }

  return query;
};

/**
 * The single source of truth for the Customer Suggestions list cache entry.
 *
 * Preserves the legacy `(1, 1000)` call. Every reader and every invalidator MUST
 * go through this constant (or `invalidateCustomerSuggestions` below) — the key
 * is derived from these options, so a second literal that drifts by a byte
 * silently addresses a different cache entry.
 */
const customerSuggestionsOptions = { query: { page: 1, limit: 1000 } } as const;

/**
 * Invalidate the Customer Suggestions list.
 *
 * Lives next to the query hook so the options literal exists in exactly one
 * place; mutations across the app call this rather than rebuilding the key.
 */
export const invalidateCustomerSuggestions = () =>
  queryClient.invalidateQueries({
    queryKey: getV1MigrationCandidateBusinessPartnersQueryKey(customerSuggestionsOptions),
  });

/**
 * Paginated migration-candidate business partners ("Customer Suggestions").
 */
export const useMigrationCandidateCustomerSuggestions = () => {
  const query = useQuery({
    ...getV1MigrationCandidateBusinessPartnersOptions(customerSuggestionsOptions),
    enabled: featureFlags.MIGRATION && !featureFlags.MIGRATION_MOCK_DATA,
  });

  if (featureFlags.MIGRATION_MOCK_DATA) {
    return { ...query, data: migrationCandidateCustomerSuggestionsMockData, isLoading: false };
  }

  return query;
};
